/**
 * @desc This page applies 508 Compliance standards on select element
 *
 * @prettier
 * @author Alexander Nagelberg
 * @author Victoria Saleem (minor updates)
 *
 **/
import React, { useMemo, useEffect, useState } from 'react'
import { useTable, usePagination } from 'react-table'

import LgyLink from 'app/components/util/lgy-link/LgyLink'
import { formatLin, formatDollars, formatUnixTime } from 'app/utils/fieldUtils'
import { URLs } from '../utils/refundUtils'

const MAX_JAVA_INTEGER = 2147483647

const RefundRecordsTable = ({ refunds, totalRefunds, updatePage, initialPageSize = 100 }) => {
  const [pageCount, setPageCount] = useState(Math.ceil(totalRefunds / initialPageSize))

  // Rows and columns need to be memoized or data backing tables will change each render
  const columns = useMemo(
    () => [
      {
        Header: '',
        accessor: 'refundId',
        Cell: ({ value }) => <LgyLink to={`${URLs.FUNDING_FEE_REFUND_APP_PATH}/${value}`}>View/Edit</LgyLink>
      },
      {
        Header: 'LIN',
        accessor: 'lin',
        Cell: ({ value }) => (
          <a href={`${URLs.WEBLGY_PATH}/LINInquiry.do?LIN=${value}`} target={value}>
            {formatLin(value)}
          </a>
        )
      },
      { Header: 'Name', accessor: 'name' },
      { Header: 'Amount', accessor: 'refundAmount' },
      { Header: 'Status', accessor: 'status' },
      { Header: 'Approval Date', accessor: 'ffpsRefundDecisionDate' }
    ],
    []
  )

  const data = useMemo(
    () =>
      refunds.map(refund => ({
        refundId: refund.id,
        lin: refund.lin,
        name: `${refund.firstName} ${refund.lastName}`,
        refundAmount: `$${formatDollars(refund.refundAmount)}`,
        status: refund.status,
        ffpsRefundDecisionDate: `${formatUnixTime(refund.ffpsRefundDecisionDate)}`
      })),
    [refunds]
  )

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headerGroups,
    page,
    canNextPage,
    nextPage,
    canPreviousPage,
    previousPage,
    setPageSize,
    state: { pageSize, pageIndex }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: initialPageSize },
      manualPagination: updatePage != null,
      pageCount
    },
    usePagination
  )

  // Updates when page info changes
  useEffect(() => {
    if (updatePage != null) {
      setPageCount(Math.ceil(totalRefunds / pageSize))
      updatePage({ page: pageIndex, limit: pageSize })
    }
  }, [pageIndex, pageSize, totalRefunds, updatePage])

  return (
    <div>
      <div className='lgy-funding-fee-records-table-results'>
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map(row => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <label htmlFor='view-by-number-records-selector'>Select Range:</label>
      <select
        className='usa-width-one-fourth'
        id='view-by-number-records-selector'
        defaultValue={pageSize}
        onChange={e => setPageSize(e.target.value === 'ALL' ? MAX_JAVA_INTEGER : parseInt(e.target.value))}
      >
        {[5, 10, 25, 100, 250, 'ALL'].map(pages => (
          <option key={pages} value={pages}>
            {pages}
          </option>
        ))}
      </select>{' '}
      records &nbsp;
      <button onClick={previousPage} disabled={!canPreviousPage}>
        {'<'} Prev {pageSize}
      </button>
      <button onClick={nextPage} disabled={!canNextPage}>
        Next {pageSize} {'>'}
      </button>
    </div>
  )
}

export default RefundRecordsTable
